import { marked, type Token, type Tokens } from 'marked'
import {
  PDFDocument,
  StandardFonts,
  rgb,
  type PDFFont,
  type PDFPage,
  type RGB,
} from 'pdf-lib'
import type { BlogDraft } from '@/lib/api/types'
import { formatDateTime, formatDateOnly } from '@/lib/utils/date'

// ---------------------------------------------------------------------------
// Shared helpers (used by exportAsHtml AND exportAsPdf)
// ---------------------------------------------------------------------------

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    draft: 'Draft',
    approved: 'Approved',
    rejected: 'Rejected',
    published: 'Published',
    scheduled: 'Scheduled',
  }
  return labels[status] ?? status
}

function stripLeadingH1(markdown: string): string {
  return markdown.replace(/^\s*#\s+.+\n*/, '')
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function decodeHtmlEntities(str: string): string {
  return str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
}

export function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60)
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

async function fetchImageAsDataUrl(url: string): Promise<string | null> {
  try {
    const resp = await fetch(url)
    if (!resp.ok) return null
    const blob = await resp.blob()
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

async function fetchImageBytes(
  url: string,
): Promise<{ bytes: Uint8Array; mime: string } | null> {
  try {
    const resp = await fetch(url)
    if (!resp.ok) return null
    const mime = resp.headers.get('content-type') ?? ''
    const buf = await resp.arrayBuffer()
    return { bytes: new Uint8Array(buf), mime }
  } catch {
    return null
  }
}

/**
 * Find all image src URLs in an HTML string, fetch as base64, replace inline.
 * Decodes HTML entities in URLs before fetching (& in S3 presigned URLs).
 *
 * Used by exportAsHtml only (kept intact for that code path).
 */
async function embedImagesInHtml(html: string): Promise<string> {
  const imgSrcRegex = /src="(https?:\/\/[^"]+)"/g
  const matches: Array<{ full: string; encodedUrl: string; decodedUrl: string }> = []

  let match: RegExpExecArray | null
  while ((match = imgSrcRegex.exec(html)) !== null) {
    const encodedUrl = match[1]
    const decodedUrl = decodeHtmlEntities(encodedUrl)
    matches.push({ full: match[0], encodedUrl, decodedUrl })
  }

  if (matches.length === 0) return html

  // Deduplicate by decoded URL
  const uniqueUrls = [...new Set(matches.map((m) => m.decodedUrl))]
  const urlMap = new Map<string, string>()
  await Promise.allSettled(
    uniqueUrls.map(async (url) => {
      const dataUrl = await fetchImageAsDataUrl(url)
      if (dataUrl) urlMap.set(url, dataUrl)
    }),
  )

  let result = html
  for (const { encodedUrl, decodedUrl } of matches) {
    const dataUrl = urlMap.get(decodedUrl)
    if (dataUrl) {
      result = result.replaceAll(`src="${encodedUrl}"`, `src="${dataUrl}"`)
    }
  }
  return result
}

function buildCandidateImagesHtml(imageUrls: string[], escaped: boolean): string {
  if (imageUrls.length === 0) return ''
  const imgs = imageUrls
    .map((url) => {
      const src = escaped ? escapeHtml(url) : url
      return `<img src="${src}" alt="Image candidate" style="width:100%;border-radius:8px;object-fit:cover;" />`
    })
    .join('\n      ')
  return `
  <div class="candidates">
    <h2 style="font-size:1rem;font-weight:600;margin-bottom:12px;color:#6b7280;">Image Candidates</h2>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
      ${imgs}
    </div>
  </div>`
}

function buildDraftHtml(draft: BlogDraft, candidateImageUrls: string[] = []): string {
  const bodyHtml = marked.parse(stripLeadingH1(draft.body), { async: false }) as string
  const heroUrl = draft.heroImageUrl ?? draft.selectedImageUrl

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(draft.title)}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1a1a2e; background: #fff; max-width: 800px; margin: 0 auto; padding: 40px 24px; line-height: 1.6; }
    h1 { font-size: 2rem; font-weight: 600; margin-bottom: 12px; }
    .meta { color: #6b7280; font-size: 0.875rem; margin-bottom: 24px; display: flex; flex-wrap: wrap; gap: 8px 16px; align-items: center; }
    .status { display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: 0.75rem; font-weight: 500; background: #e5e7eb; color: #374151; }
    .hero-img { width: 100%; max-height: 400px; object-fit: cover; border-radius: 12px; margin-bottom: 24px; }
    .body { font-size: 1rem; line-height: 1.8; }
    .body h1, .body h2, .body h3 { margin-top: 1.5em; margin-bottom: 0.5em; }
    .body p { margin-bottom: 1em; }
    .body img { max-width: 100%; border-radius: 8px; margin: 1em 0; }
    .body ul, .body ol { margin-bottom: 1em; padding-left: 1.5em; }
    .body blockquote { border-left: 3px solid #d1d5db; padding-left: 1em; color: #6b7280; margin-bottom: 1em; }
    .candidates { margin-top: 32px; margin-bottom: 24px; }
    .seo { margin-top: 40px; padding: 20px; background: #f9fafb; border-radius: 12px; border: 1px solid #e5e7eb; }
    .seo h2 { font-size: 1rem; font-weight: 600; margin-bottom: 12px; color: #6b7280; }
    .seo-row { margin-bottom: 8px; }
    .seo-label { font-weight: 500; font-size: 0.875rem; color: #374151; }
    .seo-value { font-size: 0.875rem; color: #6b7280; }
    .keywords { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
    .keyword { background: #e5e7eb; padding: 2px 8px; border-radius: 8px; font-size: 0.75rem; color: #374151; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 0.75rem; color: #9ca3af; }
  </style>
</head>
<body>
  <h1>${escapeHtml(draft.title)}</h1>
  <div class="meta">
    <span class="status">${escapeHtml(statusLabel(draft.status))}</span>
    <span>Created: ${formatDateTime(draft.createdAt)}</span>
    <span>Updated: ${formatDateTime(draft.updatedAt)}</span>
    ${draft.scheduledAt ? `<span>Scheduled: ${formatDateOnly(draft.scheduledAt)}</span>` : ''}
  </div>
  ${heroUrl ? `<img class="hero-img" src="${escapeHtml(heroUrl)}" alt="${escapeHtml(draft.heroImageAlt ?? '')}" />` : ''}
  <div class="body">${bodyHtml}</div>
  ${buildCandidateImagesHtml(candidateImageUrls, true)}
  ${draft.seo ? `
  <div class="seo">
    <h2>SEO Metadata</h2>
    ${draft.seo.title ? `<div class="seo-row"><span class="seo-label">Title:</span> <span class="seo-value">${escapeHtml(draft.seo.title)}</span></div>` : ''}
    ${draft.seo.description ? `<div class="seo-row"><span class="seo-label">Description:</span> <span class="seo-value">${escapeHtml(draft.seo.description)}</span></div>` : ''}
    ${draft.seo.keywords.length > 0 ? `<div class="seo-row"><span class="seo-label">Keywords:</span><div class="keywords">${draft.seo.keywords.map((k) => `<span class="keyword">${escapeHtml(k)}</span>`).join('')}</div></div>` : ''}
  </div>` : ''}
  <div class="footer">Exported from Agents Hub &middot; ${formatDateTime(new Date().toISOString())}</div>
</body>
</html>`
}

/**
 * Build just the inner body content (no <html>/<head>/<body> wrapper) for PDF rendering.
 * Retained for legacy callers / future browser-based exports — exportAsPdf no longer uses it.
 */
function buildPdfContent(draft: BlogDraft, candidateImageUrls: string[] = []): string {
  const bodyHtml = marked.parse(stripLeadingH1(draft.body), { async: false }) as string
  const heroUrl = draft.heroImageUrl ?? draft.selectedImageUrl

  return `
<style>
  .pdf-export, .pdf-export * { text-align: left; word-spacing: normal; letter-spacing: normal; }
  .pdf-export { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1a1a2e; line-height: 1.4; }
  .pdf-export h1 { font-size: 1.75rem; font-weight: 600; line-height: 1.2; margin-bottom: 12px; }
  .pdf-export .meta { color: #6b7280; font-size: 0.875rem; margin-bottom: 24px; display: flex; flex-wrap: wrap; gap: 8px 16px; align-items: center; }
  .pdf-export .status { display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: 0.75rem; font-weight: 500; background: #e5e7eb; color: #374151; }
  .pdf-export .hero-img { width: 100%; max-height: 400px; object-fit: cover; border-radius: 12px; margin-bottom: 24px; }
  .pdf-export .body { font-size: 1rem; line-height: 1.5; }
  .pdf-export .body h2 { font-size: 1.35rem; font-weight: 600; line-height: 1.25; margin-top: 1.5em; margin-bottom: 0.4em; }
  .pdf-export .body h3 { font-size: 1.15rem; font-weight: 600; line-height: 1.25; margin-top: 1.25em; margin-bottom: 0.35em; }
  .pdf-export .body p { margin-bottom: 0.85em; }
  .pdf-export .body img { max-width: 100%; border-radius: 8px; margin: 1em 0; }
  .pdf-export .body ul, .pdf-export .body ol { margin-bottom: 1em; padding-left: 1.5em; }
  .pdf-export .body li { margin-bottom: 0.25em; }
  .pdf-export .body blockquote { border-left: 3px solid #d1d5db; padding-left: 1em; color: #6b7280; margin-bottom: 1em; }
  .pdf-export .seo { margin-top: 40px; padding: 20px; background: #f9fafb; border-radius: 12px; border: 1px solid #e5e7eb; }
  .pdf-export .seo h2 { font-size: 1rem; font-weight: 600; margin-bottom: 12px; color: #6b7280; }
  .pdf-export .seo-row { margin-bottom: 8px; }
  .pdf-export .seo-label { font-weight: 500; font-size: 0.875rem; color: #374151; }
  .pdf-export .seo-value { font-size: 0.875rem; color: #6b7280; }
  .pdf-export .keywords { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
  .pdf-export .keyword { background: #e5e7eb; padding: 2px 8px; border-radius: 8px; font-size: 0.75rem; color: #374151; }
  .pdf-export .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 0.75rem; color: #9ca3af; }
</style>
<div class="pdf-export">
  <h1>${escapeHtml(draft.title)}</h1>
  <div class="meta">
    <span class="status">${escapeHtml(statusLabel(draft.status))}</span>
    <span>Created: ${formatDateTime(draft.createdAt)}</span>
    <span>Updated: ${formatDateTime(draft.updatedAt)}</span>
    ${draft.scheduledAt ? `<span>Scheduled: ${formatDateOnly(draft.scheduledAt)}</span>` : ''}
  </div>
  ${heroUrl ? `<img class="hero-img" src="${heroUrl}" alt="${escapeHtml(draft.heroImageAlt ?? '')}" />` : ''}
  <div class="body">${bodyHtml}</div>
  ${buildCandidateImagesHtml(candidateImageUrls, false)}
  ${draft.seo ? `
  <div class="seo">
    <h2>SEO Metadata</h2>
    ${draft.seo.title ? `<div class="seo-row"><span class="seo-label">Title:</span> <span class="seo-value">${escapeHtml(draft.seo.title)}</span></div>` : ''}
    ${draft.seo.description ? `<div class="seo-row"><span class="seo-label">Description:</span> <span class="seo-value">${escapeHtml(draft.seo.description)}</span></div>` : ''}
    ${draft.seo.keywords.length > 0 ? `<div class="seo-row"><span class="seo-label">Keywords:</span><div class="keywords">${draft.seo.keywords.map((k) => `<span class="keyword">${escapeHtml(k)}</span>`).join('')}</div></div>` : ''}
  </div>` : ''}
  <div class="footer">Exported from Agents Hub &middot; ${formatDateTime(new Date().toISOString())}</div>
</div>`
}

export async function exportAsHtml(draft: BlogDraft, candidateImageUrls: string[] = []) {
  const html = buildDraftHtml(draft, candidateImageUrls)
  const selfContainedHtml = await embedImagesInHtml(html)
  const blob = new Blob([selfContainedHtml], { type: 'text/html' })
  downloadBlob(blob, `${slugify(draft.title)}.html`)
}

// Keep export for any future caller; mark intentional unused-locals as referenced.
void buildPdfContent

// ---------------------------------------------------------------------------
// PDF rendering (pdf-lib, Path B from Phase 38 Plan 01)
//
// Replaces the earlier jsPDF `.html()` text-mode implementation. jsPDF's
// HTML pipeline had unfixable visual quality issues for this use case:
//   - defaulted to justified text → huge gaps on short heading lines
//   - inserted whitespace around punctuation ("Fails ,", "Pre - Cycle")
//   - lost inline element styling (H2s rendered as plain text)
//   - rounded backgrounds were not centered around their text
//
// pdf-lib gives us full layout control. We walk the marked AST, paginate
// manually, and draw text/images directly. Trade-off: we lose CSS layout,
// so anything that needed flex/grid (image candidates grid) renders as a
// simple stacked column. That's acceptable for the export use case.
// ---------------------------------------------------------------------------

const PAGE_WIDTH = 595.28 // A4 portrait in points
const PAGE_HEIGHT = 841.89
const MARGIN_X = 50
const MARGIN_Y = 50
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_X * 2 // 495.28

const COLOR_TEXT = rgb(0x1a / 255, 0x1a / 255, 0x2e / 255) // #1a1a2e
const COLOR_MUTED = rgb(0x6b / 255, 0x72 / 255, 0x80 / 255) // #6b7280
const COLOR_BADGE_BG = rgb(0xe5 / 255, 0xe7 / 255, 0xeb / 255) // #e5e7eb
const COLOR_BADGE_TEXT = rgb(0x37 / 255, 0x41 / 255, 0x51 / 255) // #374151
const COLOR_BORDER = rgb(0xd1 / 255, 0xd5 / 255, 0xdb / 255) // #d1d5db
const COLOR_SEO_BG = rgb(0xf9 / 255, 0xfa / 255, 0xfb / 255) // #f9fafb
const COLOR_FOOTER_LINE = rgb(0xe5 / 255, 0xe7 / 255, 0xeb / 255)

type FontSet = {
  regular: PDFFont
  bold: PDFFont
  italic: PDFFont
  boldItalic: PDFFont
}

type RenderContext = {
  pdf: PDFDocument
  fonts: FontSet
  page: PDFPage
  y: number // current y cursor (top-down: starts at PAGE_HEIGHT - MARGIN_Y, decreases)
}

function newPage(ctx: RenderContext): void {
  ctx.page = ctx.pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT])
  ctx.y = PAGE_HEIGHT - MARGIN_Y
}

function ensureSpace(ctx: RenderContext, needed: number): void {
  if (ctx.y - needed < MARGIN_Y) newPage(ctx)
}

type InlineStyle = { bold?: boolean; italic?: boolean; underline?: boolean }

type InlineRun = { text: string; style: InlineStyle }

function pickFont(fonts: FontSet, style: InlineStyle): PDFFont {
  if (style.bold && style.italic) return fonts.boldItalic
  if (style.bold) return fonts.bold
  if (style.italic) return fonts.italic
  return fonts.regular
}

// Strip characters that StandardFonts (WinAnsi) cannot encode. Replace common
// "smart" punctuation with ASCII equivalents; drop anything else outside the
// Latin-1 supplement range to avoid pdf-lib throwing.
function sanitizeWinAnsi(text: string): string {
  return text
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/[–—−]/g, '-')
    .replace(/…/g, '...')
    .replace(/ /g, ' ')
    // Drop other non-WinAnsi characters (emoji, CJK, etc.). Keep everything
    // through Latin-1 Supplement (U+00FF) which WinAnsi can encode. The
    // control chars (tab/LF/CR) are intentional allowlist entries here.
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x09\x0A\x0D\x20-\xFF]/g, '')
}

// Flatten inline marked tokens (paragraph.tokens, heading.tokens, list_item.tokens, …)
// into a flat sequence of styled runs. Walks recursively into strong/em/link/del.
function flattenInline(tokens: Token[] | undefined, base: InlineStyle = {}): InlineRun[] {
  const out: InlineRun[] = []
  if (!tokens) return out
  for (const t of tokens) {
    switch (t.type) {
      case 'text': {
        const tt = t as Tokens.Text
        if (tt.tokens && tt.tokens.length > 0) {
          out.push(...flattenInline(tt.tokens, base))
        } else {
          out.push({ text: sanitizeWinAnsi(tt.text), style: { ...base } })
        }
        break
      }
      case 'strong': {
        const tt = t as Tokens.Strong
        out.push(...flattenInline(tt.tokens, { ...base, bold: true }))
        break
      }
      case 'em': {
        const tt = t as Tokens.Em
        out.push(...flattenInline(tt.tokens, { ...base, italic: true }))
        break
      }
      case 'del': {
        const tt = t as Tokens.Del
        out.push(...flattenInline(tt.tokens, base))
        break
      }
      case 'link': {
        const tt = t as Tokens.Link
        out.push(...flattenInline(tt.tokens, { ...base, underline: true }))
        break
      }
      case 'codespan': {
        const tt = t as Tokens.Codespan
        out.push({ text: sanitizeWinAnsi(tt.text), style: { ...base } })
        break
      }
      case 'br': {
        out.push({ text: '\n', style: { ...base } })
        break
      }
      case 'escape': {
        const tt = t as Tokens.Escape
        out.push({ text: sanitizeWinAnsi(tt.text), style: { ...base } })
        break
      }
      case 'image': {
        // Inline images inside paragraphs are rare in our drafts; skip them
        // here — they'll appear in the source markdown text but we don't
        // render an inline image mid-paragraph. (Block-level images come
        // from `<p><img></p>` which marked emits as a paragraph wrapping
        // one image token; handled in renderParagraph.)
        break
      }
      default: {
        // Catch-all: pull `.text` if present.
        const tt = t as { text?: string }
        if (typeof tt.text === 'string') {
          out.push({ text: sanitizeWinAnsi(tt.text), style: { ...base } })
        }
        break
      }
    }
  }
  return out
}

// Greedy word-wrap a flat run list. Returns lines, each line a list of runs
// (with adjusted text). Words are split on whitespace; whitespace between
// words is preserved as a single space.
function wrapRuns(
  runs: InlineRun[],
  fonts: FontSet,
  fontSize: number,
  maxWidth: number,
): InlineRun[][] {
  const lines: InlineRun[][] = []
  let currentLine: InlineRun[] = []
  let currentWidth = 0

  // Expand runs into (word | space) tokens, each tagged with its style.
  type WordToken = { word: string; style: InlineStyle; isSpace: boolean; forceBreak?: boolean }
  const words: WordToken[] = []
  for (const r of runs) {
    // Handle explicit newlines from <br>.
    const segments = r.text.split('\n')
    for (let si = 0; si < segments.length; si++) {
      const seg = segments[si]
      // Split into alternating non-space/space chunks.
      const parts = seg.split(/(\s+)/)
      for (const p of parts) {
        if (p === '') continue
        const isSpace = /^\s+$/.test(p)
        words.push({ word: isSpace ? ' ' : p, style: r.style, isSpace })
      }
      if (si < segments.length - 1) {
        words.push({ word: '', style: r.style, isSpace: false, forceBreak: true })
      }
    }
  }

  const pushLine = () => {
    lines.push(currentLine)
    currentLine = []
    currentWidth = 0
  }

  for (const w of words) {
    if (w.forceBreak) {
      pushLine()
      continue
    }
    const font = pickFont(fonts, w.style)
    const wordWidth = font.widthOfTextAtSize(w.word, fontSize)

    if (w.isSpace) {
      // Don't start a line with a leading space.
      if (currentLine.length === 0) continue
      // If a single space pushes us over, treat as a soft break.
      if (currentWidth + wordWidth > maxWidth) {
        pushLine()
        continue
      }
      currentLine.push({ text: ' ', style: w.style })
      currentWidth += wordWidth
      continue
    }

    if (currentWidth + wordWidth > maxWidth && currentLine.length > 0) {
      // Trim trailing space on previous line. The popped run may have a
      // different style than the current word (e.g. a bold span ended with
      // a space and we're now wrapping a regular word), so re-measure with
      // the popped run's own font — otherwise the width subtraction drifts
      // and downstream wrapping/page-break math goes off.
      while (currentLine.length > 0 && currentLine[currentLine.length - 1].text === ' ') {
        const popped = currentLine.pop()!
        const poppedFont = pickFont(fonts, popped.style)
        currentWidth -= poppedFont.widthOfTextAtSize(popped.text, fontSize)
      }
      pushLine()
    }

    // If a single word is itself longer than maxWidth, hard-break inside it.
    if (wordWidth > maxWidth) {
      let buf = ''
      let bufWidth = 0
      for (const ch of w.word) {
        const chWidth = font.widthOfTextAtSize(ch, fontSize)
        if (bufWidth + chWidth > maxWidth && buf.length > 0) {
          currentLine.push({ text: buf, style: w.style })
          pushLine()
          buf = ch
          bufWidth = chWidth
        } else {
          buf += ch
          bufWidth += chWidth
        }
      }
      if (buf.length > 0) {
        currentLine.push({ text: buf, style: w.style })
        currentWidth = bufWidth
      }
      continue
    }

    currentLine.push({ text: w.word, style: w.style })
    currentWidth += wordWidth
  }

  if (currentLine.length > 0) pushLine()
  return lines
}

function drawRunLine(
  ctx: RenderContext,
  line: InlineRun[],
  fontSize: number,
  color: RGB,
  x: number,
): void {
  let cursorX = x
  for (const run of line) {
    const font = pickFont(ctx.fonts, run.style)
    ctx.page.drawText(run.text, {
      x: cursorX,
      y: ctx.y,
      size: fontSize,
      font,
      color,
    })
    const width = font.widthOfTextAtSize(run.text, fontSize)
    if (run.style.underline) {
      ctx.page.drawLine({
        start: { x: cursorX, y: ctx.y - 1 },
        end: { x: cursorX + width, y: ctx.y - 1 },
        thickness: 0.5,
        color,
      })
    }
    cursorX += width
  }
}

// Draw a multi-line text block with word wrapping. Advances ctx.y to below the block.
function drawWrappedRuns(
  ctx: RenderContext,
  runs: InlineRun[],
  opts: {
    fontSize: number
    lineHeight: number // multiplier of fontSize
    color?: RGB
    x?: number
    maxWidth?: number
  },
): void {
  const color = opts.color ?? COLOR_TEXT
  const x = opts.x ?? MARGIN_X
  const maxWidth = opts.maxWidth ?? CONTENT_WIDTH
  const lineGap = opts.fontSize * opts.lineHeight
  const lines = wrapRuns(runs, ctx.fonts, opts.fontSize, maxWidth)
  for (const line of lines) {
    ensureSpace(ctx, lineGap)
    ctx.y -= opts.fontSize // baseline shift
    drawRunLine(ctx, line, opts.fontSize, color, x)
    ctx.y -= lineGap - opts.fontSize // gap below baseline to next line top
  }
}

function drawWrappedText(
  ctx: RenderContext,
  text: string,
  opts: {
    fontSize: number
    lineHeight: number
    color?: RGB
    bold?: boolean
    italic?: boolean
    x?: number
    maxWidth?: number
  },
): void {
  const runs: InlineRun[] = [
    {
      text: sanitizeWinAnsi(text),
      style: { bold: opts.bold, italic: opts.italic },
    },
  ]
  drawWrappedRuns(ctx, runs, opts)
}

// Draw a rounded-rectangle badge with centered text. pdf-lib doesn't support
// true rounded corners on filled rectangles in v1.17, so we use a simple
// rectangle (visually close enough at small badge sizes).
function drawBadge(
  ctx: RenderContext,
  text: string,
  opts: { fontSize: number; paddingX: number; paddingY: number },
): { width: number; height: number } {
  const font = ctx.fonts.bold
  const safe = sanitizeWinAnsi(text)
  const textWidth = font.widthOfTextAtSize(safe, opts.fontSize)
  const textHeight = font.heightAtSize(opts.fontSize, { descender: false })
  const width = textWidth + opts.paddingX * 2
  const height = textHeight + opts.paddingY * 2
  // Draw at ctx.y (top of badge).
  const top = ctx.y
  ctx.page.drawRectangle({
    x: MARGIN_X,
    y: top - height,
    width,
    height,
    color: COLOR_BADGE_BG,
  })
  ctx.page.drawText(safe, {
    x: MARGIN_X + opts.paddingX,
    y: top - opts.paddingY - textHeight + (textHeight - opts.fontSize) / 2 + 1,
    size: opts.fontSize,
    font,
    color: COLOR_BADGE_TEXT,
  })
  return { width, height }
}

async function tryEmbedImage(
  ctx: RenderContext,
  url: string,
): Promise<{ image: ReturnType<PDFDocument['embedJpg']> extends Promise<infer T> ? T : never } | null> {
  const fetched = await fetchImageBytes(url)
  if (!fetched) return null
  try {
    if (fetched.mime.includes('png')) {
      const image = await ctx.pdf.embedPng(fetched.bytes)
      return { image: image as never }
    }
    if (fetched.mime.includes('jpeg') || fetched.mime.includes('jpg')) {
      const image = await ctx.pdf.embedJpg(fetched.bytes)
      return { image: image as never }
    }
    // Try jpg first, fall back to png by content sniffing.
    try {
      const image = await ctx.pdf.embedJpg(fetched.bytes)
      return { image: image as never }
    } catch {
      const image = await ctx.pdf.embedPng(fetched.bytes)
      return { image: image as never }
    }
  } catch {
    return null
  }
}

// Render a list of tokens as the body. Returns when all consumed.
function renderTokens(ctx: RenderContext, tokens: Token[]): void {
  for (const token of tokens) {
    switch (token.type) {
      case 'heading': {
        const t = token as Tokens.Heading
        const fontSize = t.depth === 2 ? 16 : t.depth === 3 ? 13 : t.depth === 1 ? 18 : 12
        ctx.y -= fontSize * 0.6 // top margin
        const runs = flattenInline(t.tokens).map<InlineRun>((r) => ({
          text: r.text,
          style: { ...r.style, bold: true },
        }))
        drawWrappedRuns(ctx, runs, {
          fontSize,
          lineHeight: 1.25,
        })
        ctx.y -= fontSize * 0.3 // bottom margin
        break
      }
      case 'paragraph': {
        const t = token as Tokens.Paragraph
        // A paragraph that wraps a single image token is treated as a block image.
        if (
          t.tokens &&
          t.tokens.length === 1 &&
          (t.tokens[0] as Token).type === 'image'
        ) {
          // Block-level image is handled in the async-prep phase; here we just
          // leave a placeholder. (See renderBodyAsync.)
          ctx.y -= 10
          break
        }
        const runs = flattenInline(t.tokens)
        drawWrappedRuns(ctx, runs, { fontSize: 10.5, lineHeight: 1.45 })
        ctx.y -= 6 // para spacing
        break
      }
      case 'list': {
        const t = token as Tokens.List
        const indent = 14
        let n = typeof t.start === 'number' ? t.start : 1
        for (const item of t.items) {
          const marker = t.ordered ? `${n}. ` : '• '
          // Draw marker at MARGIN_X.
          const itemRuns = flattenInline(item.tokens)
          // Inline first-line marker by prepending a styled run.
          const withMarker: InlineRun[] = [
            { text: marker, style: {} },
            ...itemRuns,
          ]
          // Hanging indent: wrap at full width minus indent, then nudge later lines.
          drawWrappedRuns(ctx, withMarker, {
            fontSize: 10.5,
            lineHeight: 1.45,
            x: MARGIN_X,
            maxWidth: CONTENT_WIDTH - indent,
          })
          ctx.y -= 3
          n++
        }
        ctx.y -= 4
        break
      }
      case 'blockquote': {
        const t = token as Tokens.Blockquote
        const indent = 18
        const startY = ctx.y
        const innerRuns = flattenInline(t.tokens as Token[])
        // Render italicised, indented.
        const italicized = innerRuns.map<InlineRun>((r) => ({
          text: r.text,
          style: { ...r.style, italic: true },
        }))
        drawWrappedRuns(ctx, italicized, {
          fontSize: 10.5,
          lineHeight: 1.45,
          color: COLOR_MUTED,
          x: MARGIN_X + indent,
          maxWidth: CONTENT_WIDTH - indent,
        })
        const endY = ctx.y
        // Border line on the same page (best effort — if blockquote spans
        // pages we just draw the visible segment).
        ctx.page.drawLine({
          start: { x: MARGIN_X + 4, y: Math.min(startY, PAGE_HEIGHT - MARGIN_Y) },
          end: { x: MARGIN_X + 4, y: Math.max(endY, MARGIN_Y) },
          thickness: 2,
          color: COLOR_BORDER,
        })
        ctx.y -= 6
        break
      }
      case 'code': {
        const t = token as Tokens.Code
        drawWrappedText(ctx, t.text, {
          fontSize: 9.5,
          lineHeight: 1.4,
          color: COLOR_MUTED,
        })
        ctx.y -= 6
        break
      }
      case 'hr': {
        ctx.y -= 6
        ensureSpace(ctx, 8)
        ctx.page.drawLine({
          start: { x: MARGIN_X, y: ctx.y },
          end: { x: MARGIN_X + CONTENT_WIDTH, y: ctx.y },
          thickness: 0.5,
          color: COLOR_BORDER,
        })
        ctx.y -= 10
        break
      }
      case 'space':
        ctx.y -= 4
        break
      default:
        break
    }
  }
}

// Walk body tokens, find inline image URLs, embed them. Returns a map of
// `href → embedded PDFImage`. We do this BEFORE rendering so the renderer
// can synchronously emit them when it encounters the placeholder.
async function preEmbedBodyImages(
  ctx: RenderContext,
  tokens: Token[],
): Promise<Map<string, Awaited<ReturnType<PDFDocument['embedJpg']>>>> {
  const urls = new Set<string>()
  const walk = (toks: Token[]) => {
    for (const t of toks) {
      if (t.type === 'image') {
        const tt = t as Tokens.Image
        if (tt.href) urls.add(decodeHtmlEntities(tt.href))
      }
      const anyT = t as { tokens?: Token[]; items?: Tokens.ListItem[] }
      if (anyT.tokens) walk(anyT.tokens)
      if (anyT.items) for (const it of anyT.items) walk(it.tokens)
    }
  }
  walk(tokens)
  const map = new Map<string, Awaited<ReturnType<PDFDocument['embedJpg']>>>()
  await Promise.allSettled(
    [...urls].map(async (u) => {
      const embed = await tryEmbedImage(ctx, u)
      if (embed) map.set(u, embed.image as never)
    }),
  )
  return map
}

// Variant of renderTokens that handles paragraph-wrapped images by drawing
// them from the pre-embedded map.
function renderTokensWithImages(
  ctx: RenderContext,
  tokens: Token[],
  images: Map<string, Awaited<ReturnType<PDFDocument['embedJpg']>>>,
): void {
  for (const token of tokens) {
    if (
      token.type === 'paragraph' &&
      (token as Tokens.Paragraph).tokens.length === 1 &&
      ((token as Tokens.Paragraph).tokens[0] as Token).type === 'image'
    ) {
      const img = (token as Tokens.Paragraph).tokens[0] as Tokens.Image
      const embed = images.get(decodeHtmlEntities(img.href))
      if (embed) {
        const dims = (embed as { scaleToFit: (w: number, h: number) => { width: number; height: number } }).scaleToFit(
          CONTENT_WIDTH,
          CONTENT_WIDTH * 0.6,
        )
        ensureSpace(ctx, dims.height + 12)
        ctx.y -= dims.height
        ctx.page.drawImage(embed as never, {
          x: MARGIN_X,
          y: ctx.y,
          width: dims.width,
          height: dims.height,
        })
        ctx.y -= 12
      }
      continue
    }
    renTokenSingle(ctx, token, images)
  }
}

function renTokenSingle(
  ctx: RenderContext,
  token: Token,
  images: Map<string, Awaited<ReturnType<PDFDocument['embedJpg']>>>,
): void {
  // Delegate the single-token cases by reusing renderTokens with a one-element list.
  // For tokens whose `.items` (lists) may contain images we recurse.
  if (token.type === 'list') {
    const t = token as Tokens.List
    // For list items containing inline images we still render text only;
    // inline-in-list images are rare for this use case.
    renderTokens(ctx, [t])
    return
  }
  if (token.type === 'blockquote') {
    const t = token as Tokens.Blockquote
    renderTokens(ctx, [t])
    return
  }
  renderTokens(ctx, [token])
  void images
}

async function renderHeader(
  ctx: RenderContext,
  draft: BlogDraft,
): Promise<void> {
  // Title
  ctx.y -= 4
  drawWrappedText(ctx, draft.title, {
    fontSize: 24,
    lineHeight: 1.2,
    bold: true,
  })
  ctx.y -= 10

  // Status badge + meta line
  const badgeSize = drawBadge(ctx, statusLabel(draft.status), {
    fontSize: 9,
    paddingX: 8,
    paddingY: 4,
  })
  // Meta text to the right of the badge.
  const metaText = `Created: ${formatDateTime(draft.createdAt)}   Updated: ${formatDateTime(draft.updatedAt)}${
    draft.scheduledAt ? `   Scheduled: ${formatDateOnly(draft.scheduledAt)}` : ''
  }`
  const metaFontSize = 9
  const metaFont = ctx.fonts.regular
  const metaSafe = sanitizeWinAnsi(metaText)
  ctx.page.drawText(metaSafe, {
    x: MARGIN_X + badgeSize.width + 10,
    y: ctx.y - badgeSize.height + (badgeSize.height - metaFontSize) / 2 + 2,
    size: metaFontSize,
    font: metaFont,
    color: COLOR_MUTED,
  })
  ctx.y -= badgeSize.height + 14
}

async function renderHeroImage(
  ctx: RenderContext,
  draft: BlogDraft,
): Promise<void> {
  const heroUrl = draft.heroImageUrl ?? draft.selectedImageUrl
  if (!heroUrl) return
  const embed = await tryEmbedImage(ctx, decodeHtmlEntities(heroUrl))
  if (!embed) return
  const img = embed.image as unknown as {
    scaleToFit: (w: number, h: number) => { width: number; height: number }
  }
  const maxHeight = 80 * (72 / 25.4) // ~80mm in pt ≈ 227pt
  const dims = img.scaleToFit(CONTENT_WIDTH, maxHeight)
  ensureSpace(ctx, dims.height + 14)
  ctx.y -= dims.height
  ctx.page.drawImage(embed.image as never, {
    x: MARGIN_X + (CONTENT_WIDTH - dims.width) / 2,
    y: ctx.y,
    width: dims.width,
    height: dims.height,
  })
  ctx.y -= 14
}

function renderSeo(ctx: RenderContext, draft: BlogDraft): void {
  if (!draft.seo) return
  const hasContent =
    draft.seo.title || draft.seo.description || draft.seo.keywords.length > 0
  if (!hasContent) return

  ctx.y -= 12
  const startY = ctx.y
  const padding = 14
  const inner = CONTENT_WIDTH - padding * 2

  // Calculate height by simulating a sub-render into a temporary context first?
  // Simpler: draw the background after we know the height. Use a two-pass
  // approach — compute approximate height by counting wrapped lines.
  const approxHeight = (() => {
    let h = padding + 18 + 8 // padding + h2 + spacer
    const measure = (text: string, font: PDFFont, size: number) => {
      // Cheap measure: split into words and count line breaks for inner width.
      const words = sanitizeWinAnsi(text).split(/\s+/)
      let line = ''
      let lines = 1
      for (const w of words) {
        const trial = line ? `${line} ${w}` : w
        if (font.widthOfTextAtSize(trial, size) > inner) {
          lines++
          line = w
        } else {
          line = trial
        }
      }
      return lines * size * 1.4
    }
    if (draft.seo.title) h += measure(`Title: ${draft.seo.title}`, ctx.fonts.regular, 9.5) + 4
    if (draft.seo.description) h += measure(`Description: ${draft.seo.description}`, ctx.fonts.regular, 9.5) + 4
    if (draft.seo.keywords.length > 0) {
      h += measure(`Keywords: ${draft.seo.keywords.join(', ')}`, ctx.fonts.regular, 9.5) + 4
    }
    h += padding
    return h
  })()

  ensureSpace(ctx, approxHeight + 8)
  // After ensureSpace, the page may have changed. Recompute startY.
  const cardTop = ctx.y
  ctx.page.drawRectangle({
    x: MARGIN_X,
    y: cardTop - approxHeight,
    width: CONTENT_WIDTH,
    height: approxHeight,
    color: COLOR_SEO_BG,
    borderColor: COLOR_FOOTER_LINE,
    borderWidth: 0.5,
  })
  ctx.y -= padding
  drawWrappedText(ctx, 'SEO Metadata', {
    fontSize: 12,
    lineHeight: 1.3,
    bold: true,
    color: COLOR_MUTED,
    x: MARGIN_X + padding,
    maxWidth: inner,
  })
  ctx.y -= 4

  const drawRow = (label: string, value: string) => {
    const runs: InlineRun[] = [
      { text: `${label}: `, style: { bold: true } },
      { text: value, style: {} },
    ]
    drawWrappedRuns(ctx, runs, {
      fontSize: 9.5,
      lineHeight: 1.4,
      color: COLOR_BADGE_TEXT,
      x: MARGIN_X + padding,
      maxWidth: inner,
    })
    ctx.y -= 2
  }

  if (draft.seo.title) drawRow('Title', draft.seo.title)
  if (draft.seo.description) drawRow('Description', draft.seo.description)
  if (draft.seo.keywords.length > 0) drawRow('Keywords', draft.seo.keywords.join(', '))

  // Pull ctx.y down to just below the card so subsequent content doesn't overlap.
  ctx.y = Math.min(ctx.y, cardTop - approxHeight) - 8
  void startY
}

function renderFooter(ctx: RenderContext): void {
  const text = `Exported from Agents Hub · ${formatDateTime(new Date().toISOString())}`
  const safe = sanitizeWinAnsi(text)
  const fontSize = 8
  // Draw at bottom of current page.
  ctx.page.drawLine({
    start: { x: MARGIN_X, y: MARGIN_Y + 18 },
    end: { x: MARGIN_X + CONTENT_WIDTH, y: MARGIN_Y + 18 },
    thickness: 0.5,
    color: COLOR_FOOTER_LINE,
  })
  ctx.page.drawText(safe, {
    x: MARGIN_X,
    y: MARGIN_Y + 6,
    size: fontSize,
    font: ctx.fonts.regular,
    color: COLOR_MUTED,
  })
}

/**
 * Build a PDF for the given draft as a `Uint8Array`. Exposed for tests so
 * we can assert on the generated bytes without going through the DOM /
 * download flow.
 */
export async function buildDraftPdfBytes(
  draft: BlogDraft,
  candidateImageUrls: string[] = [],
): Promise<Uint8Array> {
  const pdf = await PDFDocument.create()
  pdf.setTitle(draft.title)
  pdf.setProducer('Agents Hub')
  pdf.setCreator('Agents Hub Draft Export')
  pdf.setCreationDate(new Date())

  const fonts: FontSet = {
    regular: await pdf.embedFont(StandardFonts.Helvetica),
    bold: await pdf.embedFont(StandardFonts.HelveticaBold),
    italic: await pdf.embedFont(StandardFonts.HelveticaOblique),
    boldItalic: await pdf.embedFont(StandardFonts.HelveticaBoldOblique),
  }

  const firstPage = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT])
  const ctx: RenderContext = {
    pdf,
    fonts,
    page: firstPage,
    y: PAGE_HEIGHT - MARGIN_Y,
  }

  await renderHeader(ctx, draft)
  await renderHeroImage(ctx, draft)

  const bodyTokens = marked.lexer(stripLeadingH1(draft.body))
  const bodyImages = await preEmbedBodyImages(ctx, bodyTokens)
  renderTokensWithImages(ctx, bodyTokens, bodyImages)

  // Candidate image strip (stacked, full width — simpler than the HTML grid).
  if (candidateImageUrls.length > 0) {
    ctx.y -= 12
    drawWrappedText(ctx, 'Image Candidates', {
      fontSize: 12,
      lineHeight: 1.3,
      bold: true,
      color: COLOR_MUTED,
    })
    ctx.y -= 6
    for (const url of candidateImageUrls) {
      const embed = await tryEmbedImage(ctx, decodeHtmlEntities(url))
      if (!embed) continue
      const img = embed.image as unknown as {
        scaleToFit: (w: number, h: number) => { width: number; height: number }
      }
      const dims = img.scaleToFit(CONTENT_WIDTH, 200)
      ensureSpace(ctx, dims.height + 10)
      ctx.y -= dims.height
      ctx.page.drawImage(embed.image as never, {
        x: MARGIN_X,
        y: ctx.y,
        width: dims.width,
        height: dims.height,
      })
      ctx.y -= 10
    }
  }

  renderSeo(ctx, draft)

  // Footer on every page.
  for (const page of pdf.getPages()) {
    const original = ctx.page
    ctx.page = page
    renderFooter(ctx)
    ctx.page = original
  }

  return await pdf.save()
}

export async function exportAsPdf(
  draft: BlogDraft,
  candidateImageUrls: string[] = [],
) {
  const bytes = await buildDraftPdfBytes(draft, candidateImageUrls)
  const blob = new Blob([bytes as BlobPart], { type: 'application/pdf' })
  downloadBlob(blob, `${slugify(draft.title)}.pdf`)
}
